import createCachedSelector from 're-reselect';

import { getPersistedState } from '../../persistedState';
import {
  EcsProjectTeamName,
  Experiment,
  ExperimentTreatmentValue,
  YammerState,
  experimentConfigs,
} from '../../state/types';
import { reportEcsExperimentTreatment } from '../../telemetry';
import { getEcsConfig } from '../ecs/selectors';

const getTreatments = (state: YammerState) => state.ecsExperiments.treatments;
const getTreatmentsFromService = (state: YammerState) => state.ecsExperiments.treatmentsFromService;
const getTreatmentOverrides = (state: YammerState) => state.ecsExperiments.treatmentOverrides;

const hasValue = (treatment?: ExperimentTreatmentValue): treatment is ExperimentTreatmentValue => treatment != null;

const getTreatmentOrDefault = (
  treatment: ExperimentTreatmentValue | undefined,
  experiment: Experiment
): ExperimentTreatmentValue => (hasValue(treatment) ? treatment : experimentConfigs[experiment].defaultTreatment);

type IsUserTreatedInExperiment = (state: YammerState, experiment: Experiment) => boolean;
export const isUserTreatedInExperiment: IsUserTreatedInExperiment = (state, experiment) => {
  const treatmentFromService = getTreatmentsFromService(state)[experiment];

  return hasValue(treatmentFromService);
};

type GetExperimentTreatment = (state: YammerState, experiment: Experiment) => ExperimentTreatmentValue;
export const getExperimentTreatment: GetExperimentTreatment = (state, experiment) => getTreatments(state)[experiment];

type GetExperimentTreatmentIfActive = (
  state: YammerState,
  experiment: Experiment
) => ExperimentTreatmentValue | undefined;
export const getExperimentTreatmentIfActive: GetExperimentTreatmentIfActive = (state, experiment) =>
  getTreatmentOverrides(state)[experiment] ?? getTreatmentsFromService(state)[experiment];

const getPersistedExperimentTreatments = async (key: string) => {
  const state = await getPersistedState(key);

  return state?.ecsExperiments;
};

type GetAndReportExperiment = (state: YammerState, experiment: Experiment) => ExperimentTreatmentValue;
export const getAndReportExperiment: GetAndReportExperiment = (state, experiment) => {
  const treatment = getExperimentTreatment(state, experiment);
  const shouldReportUserTreatedEvent = shouldReportExperimentTreatedEvent(state, experiment);

  if (shouldReportUserTreatedEvent) {
    const etag = getEcsConfig(state).etag;
    const projectTeam = experimentConfigs[experiment].projectTeam;
    reportEcsExperimentTreatment(experiment, treatment, projectTeam, etag);
  }

  return treatment;
};

type GetPersistedExperimentTreatment = (key: string, experiment: Experiment) => Promise<ExperimentTreatmentValue>;
export const getPersistedExperimentTreatment: GetPersistedExperimentTreatment = async (key, experiment) => {
  const experiments = await getPersistedExperimentTreatments(key);

  return getTreatmentOrDefault(experiments?.treatments[experiment], experiment);
};

type ShouldReportExperimentTreatedEvent = (state: YammerState, experiment: Experiment) => boolean;
export const shouldReportExperimentTreatedEvent: ShouldReportExperimentTreatedEvent = (state, experiment) => {
  const treatment = getExperimentTreatment(state, experiment);
  const treatmentFromService = getTreatmentsFromService(state)[experiment];
  const isUserTreated = hasValue(treatmentFromService);

  const isUsingServerTreatment = treatment === treatmentFromService;

  return isUserTreated && isUsingServerTreatment;
};

type ShouldReportPersistedExperimentTreatedEvent = (key: string, experiment: Experiment) => Promise<boolean>;
export const shouldReportPersistedExperimentTreatedEvent: ShouldReportPersistedExperimentTreatedEvent = async (
  key,
  experiment
) => {
  const experiments = await getPersistedExperimentTreatments(key);
  if (!experiments) {
    return false;
  }

  const treatment = experiments.treatments[experiment];
  const treatmentFromService = experiments.treatmentsFromService[experiment];
  const isUserTreated = hasValue(treatmentFromService);

  const isUsingServerTreatment = treatment === treatmentFromService;

  return isUserTreated && isUsingServerTreatment;
};

type HasExperimentOverride = (state: YammerState, id: Experiment) => boolean;
export const hasExperimentOverride: HasExperimentOverride = (state, id) =>
  getTreatmentOverrides(state)[id] !== undefined;

export const getExperimentsOverview = createCachedSelector(
  getTreatments,
  getTreatmentOverrides,
  (treatments, overrides) =>
    (Object.keys(treatments) as Experiment[])
      .filter((name: Experiment) => name in Experiment)
      .map((name: Experiment) => ({
        name,
        hasLocalOverride: overrides[name as Experiment] !== undefined,
        isControl: treatments[name as Experiment] === experimentConfigs[name as Experiment].defaultTreatment,
      }))
)(() => 'cache');

export const getClientForwardExperiments = createCachedSelector(getTreatments, (treatments) =>
  (Object.keys(treatments) as Experiment[]).filter(
    (experiment) => experimentConfigs[experiment]?.projectTeam === EcsProjectTeamName.ClientForward
  )
)(() => 'cache');
